/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Design Automation team for Inventor
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that the above copyright notice appears in all copies and
// that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////

import React, { Component } from 'react';
import { connect } from 'react-redux';
import './parametersContainer.css';
import Parameter from './parameter';
import {
    getActiveProject,
    getParameters,
    getUpdateParameters,
    modalProgressShowing,
    updateFailedShowing,
    errorData
} from '../reducers/mainReducer';
import {
    fetchParameters,
    resetParameters,
    updateModelWithParameters
} from '../actions/parametersActions';
import {
    showModalProgress,
    showUpdateFailed,
    invalidateDrawing
} from '../actions/uiFlagsActions';
import Button from '@hig/button';
import Tooltip from '@hig/tooltip';
import { Alert24 } from "@hig/icons";

import ModalProgress from './modalProgress';
import ModalFail from './modalFail';
import { fullWarningMsg } from '../utils/conversion';

export class ParametersContainer extends Component {
    componentDidMount() {
        if (this.props.activeProject && this.props.activeProject.id) {
            this.props.fetchParameters(this.props.activeProject.id);
        }
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.activeProject &&
            prevProps.activeProject &&
            this.props.activeProject.id !== prevProps.activeProject.id
        ) {
            this.props.fetchParameters(this.props.activeProject.id);
        }
    }

    updateClicked() {
        this.props.updateModelWithParameters(
            this.props.activeProject.id,
            this.props.projectUpdateParameters
        );

        this.props.invalidateDrawing();
    }

    onUpdateFailedCloseClick() {
        this.props.showUpdateFailed(false);
    }

    onModalProgressClose() {
        this.props.hideModalProgress();
    }

    onResetClick = () => {
        this.props.resetParameters(this.props.activeProject.id);
        this.props.invalidateDrawing();
    };

    render() {
        const parameterList = this.props.activeProject
            ? this.props.projectUpdateParameters
            : [];

        const buttonsContainerClass = parameterList
            ? 'buttonsContainer'
            : 'buttonsContainer hidden';

        const adoptWarning = this.props.adoptWarning;
        const tooltipProps = adoptWarning
            ? {
                openOnHover: true,
                content: () => (
                    <div className="warningButtonTooltip">
                        {adoptWarning}
                    </div>
                )
            }
            : { open: false };

        const updateIcon = adoptWarning
            ? <Alert24 style={{ color: 'white' }} />
            : undefined;

        return (
            <div className="parametersContainer">
                <div className="pencilContainer" />

                <div className="parameters">
                    {parameterList
                        ? parameterList.map((parameter, index) => (
                            <Parameter
                                key={index}
                                parameter={parameter}
                                allParameters={parameterList}
                            />
                        ))
                        : 'Brak parametrów.'}
                </div>

                <hr className="parametersSeparator" />

                <div className={buttonsContainerClass}>
                    <Button
                        style={{ width: '125px' }}
                        size="standard"
                        title="Resetuj"
                        type="secondary"
                        width="grow"
                        onClick={this.onResetClick}
                    />

                    <div style={{ width: '14px' }} />

                    <div width="grow">
                        <Tooltip
                            {...tooltipProps}
                            className="paramTooltip"
                            anchorPoint="top-center"
                        >
                            <Button
                                id="updateButton"
                                className="orangeButton"
                                icon={updateIcon}
                                style={{ width: '125px' }}
                                size="standard"
                                title="Aktualizuj"
                                width="grow"
                                onClick={() => this.updateClicked()}
                            />
                        </Tooltip>
                    </div>

                    {this.props.modalProgressShowing && (
                        <ModalProgress
                            open={this.props.modalProgressShowing}
                            title="Aktualizowanie projektu"
                            doneTitle="Aktualizowanie zakończone"
                            label={this.props.activeProject.id}
                            icon="/Assembly_icon.svg"
                            onClose={() => this.onModalProgressClose()}
                            warningMsg={this.props.adoptWarning}
                        />
                    )}

                    {this.props.updateFailedShowing && (
                        <ModalFail
                            open={this.props.updateFailedShowing}
                            title="Aktualizowanie nie powiodło się"
                            contentName="Project:"
                            label={this.props.activeProject.id}
                            onClose={() => this.onUpdateFailedCloseClick()}
                            errorData={this.props.errorData}
                        />
                    )}
                </div>
            </div>
        );
    }
}

/* istanbul ignore next */
export default connect(function (store) {
    const activeProject = getActiveProject(store);
    const adoptWarning = fullWarningMsg(activeProject.adoptWarnings);

    return {
        activeProject: activeProject,
        modalProgressShowing: modalProgressShowing(store),
        updateFailedShowing: updateFailedShowing(store),
        errorData: errorData(store),
        projectSourceParameters: getParameters(activeProject.id, store),
        projectUpdateParameters: getUpdateParameters(activeProject.id, store),
        adoptWarning: adoptWarning
    };
}, {
    fetchParameters,
    resetParameters,
    updateModelWithParameters,
    showModalProgress,
    showUpdateFailed,
    invalidateDrawing,
    hideModalProgress: () => async (dispatch) => {
        dispatch(showModalProgress(false));
    }
})(ParametersContainer);